<template>
  <div class="qrcode-page">
    <!-- ==================== 生成面板 ==================== -->
    <el-card class="generate-card">
      <div slot="header">
        <span><i class="el-icon-plus"></i> 生成二维码</span>
      </div>

      <el-row :gutter="30">
        <!-- 左侧：表单 -->
        <el-col :span="10">
          <el-form label-width="100px" size="small">

            <!-- 场景类型 -->
            <el-form-item label="场景类型" required>
              <el-radio-group v-model="form.sceneType" @change="onSceneChange">
                <el-radio label="shop">店铺支付</el-radio>
                <el-radio label="product">商品支付</el-radio>
                <el-radio label="activity">活动</el-radio>
                <el-radio label="index">首页</el-radio>
              </el-radio-group>
            </el-form-item>

            <!-- 目标ID - 店铺支付（自动使用当前店铺ID，不可编辑） -->
            <el-form-item label="目标ID" required v-if="form.sceneType === 'shop'">
              <el-input
                  :value="currentShopId ? `当前店铺：${currentShopId}` : '请先登录'"
                  disabled
                  style="width:100%;"
              />
              <div class="form-tip" style="color:#409eff;font-weight:500;">
                当前店铺ID：{{ currentShopId || '未登录' }}
              </div>
            </el-form-item>

            <!-- 目标ID - 商品支付 -->
            <el-form-item label="目标ID" required v-else-if="form.sceneType === 'product'">
              <el-input-number
                  v-model="form.targetId"
                  :min="1"
                  :max="99999999"
                  style="width:100%;"
                  placeholder="请输入商品ID"
              />
              <div class="form-tip">商品ID，扫码后将跳转到商品详情页</div>
            </el-form-item>

            <!-- 目标ID - 活动 -->
            <el-form-item label="目标ID" required v-else-if="form.sceneType === 'activity'">
              <el-input-number
                  v-model="form.targetId"
                  :min="1"
                  :max="99999999"
                  style="width:100%;"
                  placeholder="请输入活动ID"
              />
              <div class="form-tip">活动ID，扫码后将跳转到活动页</div>
            </el-form-item>

            <!-- 目标ID - 首页（不需要输入） -->
            <el-form-item label="目标ID" v-else>
              <el-input
                  value="无需输入"
                  disabled
                  style="width:100%;"
              />
              <div class="form-tip">首页二维码无需目标ID</div>
            </el-form-item>

            <!-- 跳转页面（自动生成，不可编辑） -->
            <el-form-item label="跳转页面">
              <el-input
                  :value="defaultPage"
                  disabled
                  style="width:100%;"
              />
              <div class="form-tip">
                固定页面，根据场景自动匹配
              </div>
            </el-form-item>

            <!-- 二维码名称 -->
            <el-form-item label="二维码名称">
              <el-input
                  v-model="form.name"
                  placeholder="如：门店收款码-01"
                  maxlength="30"
                  style="width:100%;"
              />
            </el-form-item>

            <!-- 描述 -->
            <el-form-item label="描述">
              <el-input
                  v-model="form.description"
                  placeholder="选填，便于识别"
                  maxlength="100"
                  style="width:100%;"
              />
            </el-form-item>

            <!-- 宽度 -->
            <el-form-item label="二维码尺寸">
              <el-slider
                  v-model="form.width"
                  :min="200"
                  :max="600"
                  :step="10"
                  show-input
                  input-size="small"
                  style="width:80%;"
              />
              <span style="margin-left:10px;color:#999;font-size:12px;">{{ form.width }}px</span>
            </el-form-item>

            <!-- 按钮 -->
            <el-form-item>
              <el-button type="primary" @click="handleGenerate" :loading="generating">
                <i class="el-icon-check"></i> 生成
              </el-button>
              <el-button @click="resetForm">
                <i class="el-icon-refresh"></i> 重置
              </el-button>
            </el-form-item>

          </el-form>
        </el-col>

        <!-- 右侧：预览 -->
        <el-col :span="14">
          <div class="qrcode-preview" v-if="qrcodeData">
            <div class="qrcode-image-wrapper">
              <img :src="qrcodeData.qrcodeUrl" alt="二维码" class="qrcode-image" />
            </div>
            <div class="qrcode-info">
              <el-descriptions :column="2" size="small" border>
                <el-descriptions-item label="场景">{{ sceneTypeLabel }}</el-descriptions-item>
                <el-descriptions-item label="目标ID">{{ qrcodeData.targetId || '-' }}</el-descriptions-item>
                <el-descriptions-item label="二维码Key" :span="2">
                  <el-tag size="small">{{ qrcodeData.qrcodeKey }}</el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="跳转页面" :span="2">
                  <code style="font-size:12px;color:#1989fa;">{{ qrcodeData.page || '默认' }}</code>
                </el-descriptions-item>
                <el-descriptions-item label="名称">{{ qrcodeData.name || '-' }}</el-descriptions-item>
                <el-descriptions-item label="状态">
                  <el-tag type="success" size="mini">有效</el-tag>
                </el-descriptions-item>
              </el-descriptions>
            </div>
            <div class="qrcode-actions">
              <el-button type="primary" size="small" @click="downloadQrcode">
                <i class="el-icon-download"></i> 下载
              </el-button>
              <el-button size="small" @click="copyQrcodeKey">
                <i class="el-icon-document-copy"></i> 复制Key
              </el-button>
              <el-button size="small" @click="showScenePreview">
                <i class="el-icon-view"></i> 查看参数
              </el-button>
            </div>
          </div>
          <div class="qrcode-empty" v-else>
            <i class="el-icon-picture-outline"></i>
            <p>填写参数后点击「生成」</p>
            <p style="font-size:12px;color:#c0c4cc;">生成的二维码将保存到列表中</p>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- ==================== 列表 ==================== -->
    <el-card class="list-card">
      <div slot="header">
        <span><i class="el-icon-list"></i> 二维码列表</span>
        <el-button size="small" style="float:right;" @click="loadRecords">
          <i class="el-icon-refresh"></i> 刷新
        </el-button>
      </div>

      <!-- 搜索条件 -->
      <div class="filter-bar">
        <el-select v-model="filter.sceneType" placeholder="全部场景" clearable size="small" @change="loadRecords">
          <el-option label="全部" value="" />
          <el-option label="店铺支付" value="shop" />
          <el-option label="商品支付" value="product" />
          <el-option label="活动" value="activity" />
          <el-option label="首页" value="index" />
        </el-select>
        <el-input
            v-model="filter.keyword"
            placeholder="搜索名称/描述/Key"
            size="small"
            style="width:200px;margin-left:10px;"
            clearable
            @keyup.enter="loadRecords"
            @clear="loadRecords"
        />
        <el-button size="small" type="primary" style="margin-left:10px;" @click="loadRecords">
          搜索
        </el-button>
      </div>

      <!-- 表格 -->
      <div v-loading="tableLoading">
        <el-table :data="recordList" border size="small" stripe>
          <el-table-column prop="id" label="ID" width="60" align="center" />
          <el-table-column label="场景" width="90" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="getSceneTypeTag(row.sceneType)">
                {{ getSceneTypeLabel(row.sceneType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="targetId" label="目标ID" width="90" align="center" />
          <el-table-column prop="name" label="名称" min-width="120" show-overflow-tooltip />
          <el-table-column prop="description" label="描述" min-width="100" show-overflow-tooltip />
          <el-table-column label="跳转页面" min-width="150" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <code style="font-size:11px;color:#666;">{{ row.page || '默认' }}</code>
            </template>
          </el-table-column>
          <el-table-column label="二维码Key" min-width="130" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <el-tag size="mini" type="info">{{ row.qrcodeKey }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="160" align="center">
            <template slot-scope="{ row }">
              {{ formatTime(row.addDataTime) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="70" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
                {{ row.status === 1 ? '有效' : '失效' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" size="mini" @click="viewQrcode(row)">
                查看
              </el-button>
              <el-button type="text" size="mini" @click="downloadRowQrcode(row)">
                下载
              </el-button>
              <el-button type="text" size="mini" style="color:#f56c6c;" @click="deleteRecord(row)">
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
              :current-page="pagination.currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              :total="pagination.total"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- ==================== 查看参数弹窗 ==================== -->
    <el-dialog title="二维码参数详情" :visible.sync="dialogVisible" width="500px">
      <el-descriptions :column="1" border>
        <el-descriptions-item label="场景">{{ sceneTypeLabel }}</el-descriptions-item>
        <el-descriptions-item label="目标ID">{{ qrcodeData?.targetId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="二维码Key">{{ qrcodeData?.qrcodeKey }}</el-descriptions-item>
        <el-descriptions-item label="跳转页面">
          <code>{{ qrcodeData?.page || '默认' }}</code>
        </el-descriptions-item>
        <el-descriptions-item label="名称">{{ qrcodeData?.name || '-' }}</el-descriptions-item>
        <el-descriptions-item label="描述">{{ qrcodeData?.description || '-' }}</el-descriptions-item>
        <el-descriptions-item label="尺寸">{{ qrcodeData?.width || 430 }}px</el-descriptions-item>
        <el-descriptions-item label="状态">{{ qrcodeData?.status === 1 ? '有效' : '失效' }}</el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="dialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'QrcodeManage',

  data() {
    return {
      // ===== 表单 =====
      form: {
        sceneType: 'shop',
        targetId: null,
        page: '',
        name: '',
        description: '',
        width: 430
      },

      // ===== 当前店铺信息 =====
      currentShopId: null,
      currentShopName: '',
      userInfo: null,

      // ===== 生成结果 =====
      qrcodeData: null,
      generating: false,

      // ===== 列表 =====
      recordList: [],
      tableLoading: false,
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },

      // ===== 筛选 =====
      filter: {
        sceneType: '',
        keyword: ''
      },

      // ===== 弹窗 =====
      dialogVisible: false
    }
  },

  computed: {
    sceneTypeLabel() {
      const map = {
        shop: '店铺支付',
        product: '商品支付',
        activity: '活动',
        index: '首页'
      }
      return map[this.form.sceneType] || this.form.sceneType
    },

    defaultPage() {
      const map = {
        shop: 'pages/pay/index',
        product: 'pages/product/detail',
        activity: 'pages/activity/index',
        index: 'pages/index/index'
      }
      return map[this.form.sceneType] || 'pages/index/index'
    },

    // 场景是否需要目标ID
    needTargetId() {
      return this.form.sceneType === 'shop' ||
          this.form.sceneType === 'product' ||
          this.form.sceneType === 'activity'
    }
  },

  mounted() {
    // 加载用户信息和当前店铺ID
    this.loadUserInfo()
    this.loadRecords()
  },

  methods: {

    // ==================== 加载用户信息 ====================
    async loadUserInfo() {
      try {
        const cachedUserInfo = localStorage.getItem('user_info')
        if (cachedUserInfo) {
          try {
            this.userInfo = JSON.parse(cachedUserInfo)
            this.currentShopId = this.userInfo?.merchantId || null
            this.currentShopName = this.userInfo?.merchantName || ''
            console.log('当前店铺ID:', this.currentShopId)
            console.log('当前店铺名称:', this.currentShopName)

            // 自动填充店铺ID
            if (this.currentShopId) {
              this.form.targetId = this.currentShopId
              // 自动生成名称
              if (!this.form.name) {
                this.form.name = `${this.currentShopName || '店铺'}-收款码`
              }
            }
            return
          } catch (e) {
            console.error('解析缓存失败:', e)
          }
        }

        // 从接口获取用户信息
        const res = await api.login_getAdminInfo(null, {})
        if (res.data.code === '000' && res.data.data) {
          this.userInfo = res.data.data
          this.currentShopId = this.userInfo?.merchantId || null
          this.currentShopName = this.userInfo?.merchantName || ''
          localStorage.setItem('user_info', JSON.stringify(this.userInfo))

          // 自动填充店铺ID
          if (this.currentShopId) {
            this.form.targetId = this.currentShopId
            if (!this.form.name) {
              this.form.name = `${this.currentShopName || '店铺'}-收款码`
            }
          }
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
      }
    },

    // ==================== 场景切换 ====================
    onSceneChange() {
      this.form.page = ''
      // 切换到首页时清空目标ID
      if (this.form.sceneType === 'index') {
        this.form.targetId = null
      }
      // 切换到店铺时自动填充当前店铺ID
      if (this.form.sceneType === 'shop') {
        this.form.targetId = this.currentShopId
        if (!this.form.name && this.currentShopName) {
          this.form.name = `${this.currentShopName}-收款码`
        }
      }
    },

    // ==================== 生成 ====================
    async handleGenerate() {
      // 校验目标ID（店铺、商品、活动需要）
      if (this.needTargetId && (!this.form.targetId || this.form.targetId <= 0)) {
        const tipMap = {
          shop: '请先登录获取店铺ID',
          product: '请输入商品ID',
          activity: '请输入活动ID'
        }
        this.$message.warning(tipMap[this.form.sceneType] || '请输入目标ID')
        return
      }

      // 店铺场景必须有店铺ID
      if (this.form.sceneType === 'shop' && !this.form.targetId) {
        this.$message.warning('请先登录获取店铺ID')
        return
      }

      const page = this.defaultPage  // 固定使用默认页面

      this.generating = true
      try {
        // 构建基础参数
        const params = {
          targetId: this.form.targetId || 0,
          page: page,
          name: this.form.name || `${this.sceneTypeLabel}-${this.form.targetId || 'index'}`,
          description: this.form.description,
          width: this.form.width
        }

        // 根据不同场景调用不同接口
        let res
        switch (this.form.sceneType) {
          case 'shop':
            params.shopId = this.form.targetId
            res = await api.qrcode_shopCodeSave(params)
            break
          case 'product':
            params.productId = this.form.targetId
            res = await api.qrcode_productCodeSave(params)
            break
          case 'activity':
            params.activityId = this.form.targetId
            res = await api.qrcode_activityCodeSave(params)
            break
          default:
            // 首页
            res = await api.qrcode_saveCode(params)
        }

        const responseData = res && res.data ? res.data : res
        const code = responseData.code !== undefined ? String(responseData.code) : ''

        if (code === '000' && responseData.data) {
          this.qrcodeData = {
            ...responseData.data,
            width: this.form.width,
            status: 1,
            sceneType: this.form.sceneType,
            targetId: this.form.targetId,
            page: page
          }
          this.$message.success('二维码生成成功')
          this.loadRecords()
        } else {
          this.$message.error(responseData.message || '生成失败')
        }
      } catch (error) {
        console.error('生成失败:', error)
        this.$message.error('生成失败，请重试')
      } finally {
        this.generating = false
      }
    },

    // ==================== 列表 ====================
    async loadRecords() {
      this.tableLoading = true
      try {
        const res = await api.qrcode_newList({
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
          sceneType: this.filter.sceneType || undefined
        })

        const responseData = res && res.data ? res.data : res
        const code = responseData.code !== undefined ? String(responseData.code) : ''

        if (code === '000' && responseData.data) {
          const data = responseData.data
          let list = data.list || []
          if (this.filter.keyword) {
            const kw = this.filter.keyword.toLowerCase()
            list = list.filter(item =>
                (item.name && item.name.toLowerCase().includes(kw)) ||
                (item.description && item.description.toLowerCase().includes(kw)) ||
                (item.qrcodeKey && item.qrcodeKey.toLowerCase().includes(kw))
            )
          }
          this.recordList = list
          this.pagination.total = data.totalCount || data.total || list.length
        } else {
          this.recordList = []
          this.pagination.total = 0
        }
      } catch (error) {
        console.error('加载记录失败:', error)
        this.recordList = []
        this.pagination.total = 0
      } finally {
        this.tableLoading = false
      }
    },

    // ==================== 查看 ====================
    viewQrcode(row) {
      this.qrcodeData = { ...row }
      this.form.sceneType = row.sceneType || 'shop'
      this.form.targetId = row.targetId
      this.form.page = row.page || ''
      this.form.name = row.name || ''
      this.form.description = row.description || ''
    },

    showScenePreview() {
      this.dialogVisible = true
    },

    // ==================== 下载 ====================
    downloadQrcode() {
      if (!this.qrcodeData?.qrcodeUrl) {
        this.$message.warning('请先生成二维码')
        return
      }
      this.triggerDownload(this.qrcodeData.qrcodeUrl, `qrcode_${this.qrcodeData.qrcodeKey || Date.now()}`)
    },

    downloadRowQrcode(row) {
      if (!row.qrcodeUrl) {
        this.$message.warning('无图片')
        return
      }
      this.triggerDownload(row.qrcodeUrl, `qrcode_${row.qrcodeKey || row.id}`)
    },

    triggerDownload(url, fileName) {
      const a = document.createElement('a')
      a.href = url
      a.download = `${fileName}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },

    // ==================== 复制 ====================
    copyQrcodeKey() {
      if (!this.qrcodeData?.qrcodeKey) {
        this.$message.warning('请先生成二维码')
        return
      }
      const text = this.qrcodeData.qrcodeKey
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制二维码Key')
      }).catch(() => {
        const textarea = document.createElement('textarea')
        textarea.value = text
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.$message.success('已复制二维码Key')
      })
    },

    // ==================== 删除 ====================
    async deleteRecord(row) {
      try {
        await this.$confirm(`确定要删除「${row.name || row.qrcodeKey}」吗？`, '提示', {
          type: 'warning'
        })
        const res = await api.qrcode_deleteCode({ id: row.id })
        const responseData = res && res.data ? res.data : res
        const code = responseData.code !== undefined ? String(responseData.code) : ''

        if (code === '000') {
          this.$message.success('删除成功')
          if (this.qrcodeData?.id === row.id) {
            this.qrcodeData = null
          }
          this.loadRecords()
        } else {
          this.$message.error(responseData.message || '删除失败')
        }
      } catch (error) {
        // 用户取消
      }
    },

    // ==================== 分页 ====================
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1
      this.loadRecords()
    },

    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.loadRecords()
    },

    // ==================== 工具 ====================
    resetForm() {
      this.form.sceneType = 'shop'
      this.form.targetId = this.currentShopId
      this.form.page = ''
      this.form.name = this.currentShopName ? `${this.currentShopName}-收款码` : ''
      this.form.description = ''
      this.form.width = 430
    },

    formatTime(timestamp) {
      if (!timestamp) return '-'
      const d = new Date(timestamp)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },

    getSceneTypeLabel(type) {
      const map = {
        shop: '店铺支付',
        product: '商品支付',
        activity: '活动',
        index: '首页'
      }
      return map[type] || type
    },

    getSceneTypeTag(type) {
      const map = {
        shop: 'primary',
        product: 'success',
        activity: 'warning',
        index: 'info'
      }
      return map[type] || ''
    }
  }
}
</script>

<style scoped>
.qrcode-page {
  padding: 20px;
}

.generate-card,
.list-card {
  margin-bottom: 20px;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  line-height: 1.5;
}

.qrcode-preview {
  text-align: center;
  padding: 30px 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.qrcode-image-wrapper {
  width: 200px;
  height: 200px;
  margin: 0 auto 20px;
  padding: 10px;
  border: 2px solid #1989fa;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qrcode-image {
  width: 180px;
  height: 180px;
  object-fit: contain;
}

.qrcode-info {
  text-align: left;
  margin-bottom: 15px;
}

.qrcode-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.qrcode-empty {
  text-align: center;
  padding: 80px 20px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  color: #c0c4cc;
}

.qrcode-empty i {
  font-size: 60px;
  margin-bottom: 15px;
  display: block;
}

.qrcode-empty p {
  font-size: 14px;
  margin: 4px 0;
}

.filter-bar {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

@media screen and (max-width: 768px) {
  .qrcode-preview {
    min-height: auto;
    padding: 20px;
  }

  .qrcode-image-wrapper {
    width: 160px;
    height: 160px;
  }

  .qrcode-image {
    width: 140px;
    height: 140px;
  }

  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-bar .el-select,
  .filter-bar .el-input {
    width: 100% !important;
    margin-left: 0 !important;
    margin-bottom: 10px;
  }
}
</style>